import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { OpenInNew } from "@mui/icons-material";
import ExpandableTagsBox, {
  AddTagsDialog,
} from "../../../shared-components/ExpandableTagsBox";
import { useApiClient } from "../../../api";
import { enqueueSnackbar } from "notistack";
import { format } from "date-fns";
import { formatSourceType, formatFileSize } from "../utils/formatters";

const RagDocumentViewer = ({ open, onClose, document, onRefetch }) => {
  const ApiClient = useApiClient();
  const [addTagsOpen, setAddTagsOpen] = useState(false);
  const [newTags, setNewTags] = useState([]);
  const [savingTags, setSavingTags] = useState(false);

  if (!document) return null;

  const handleApproveTags = async () => {
    if (!newTags || newTags.length === 0) {
      setAddTagsOpen(false);
      return;
    }
    setSavingTags(true);
    try {
      const existingTagStrings = displayTags.map((tag) =>
        typeof tag === "string" ? tag : tag.tag || tag,
      );
      const allTags = [...existingTagStrings, ...newTags];

      const response = await ApiClient.rag.approveDocumentTags({
        ragUpload: document.id,
        tags: allTags,
      });

      if (response.success) {
        enqueueSnackbar(response.message || "Tags approved successfully", {
          variant: "success",
        });
        setNewTags([]);
        setAddTagsOpen(false);
        onRefetch();
      } else {
        enqueueSnackbar(response.message || "Failed to approve tags", {
          variant: "error",
        });
      }
    } catch (e) {
      console.error("Failed to approve tags", e);
      enqueueSnackbar("Error approving tags", { variant: "error" });
    } finally {
      setSavingTags(false);
    }
  };

  const handleViewDocument = () => {
    if (document.fileUrl) {
      window.open(document.fileUrl, "_blank");
    }
  };

  const displayTags = document.tagsApproved
    ? document.approvedTags || []
    : document.suggestedTags || [];

  const formattedTags = displayTags.map((tag) => ({ tag }));

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Document Details</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid size={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Filename
              </Typography>
              <Typography variant="body1">
                {document.originalFilename}
              </Typography>
            </Grid>

            <Grid size={6}>
              <Typography variant="subtitle2" color="text.secondary">
                File Type
              </Typography>
              <Typography variant="body1" sx={{ textTransform: "uppercase" }}>
                {document.fileType}
              </Typography>
            </Grid>

            <Grid size={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Source Type
              </Typography>
              <Typography variant="body1">
                {formatSourceType(document.sourceType)}
              </Typography>
            </Grid>

            <Grid size={6}>
              <Typography variant="subtitle2" color="text.secondary">
                File Size
              </Typography>
              <Typography variant="body1">
                {formatFileSize(document.fileSize)}
              </Typography>
            </Grid>

            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Uploaded
              </Typography>
              <Typography variant="body1">
                {format(new Date(document.createdAt), "MMM d, yyyy HH:mm")}
              </Typography>
            </Grid>

            <Grid size={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                {document.tagsApproved ? "Approved Tags" : "Suggested Tags"}
              </Typography>
              <ExpandableTagsBox
                tags={formattedTags}
                excludeTitle
                allowAddTags
                onAddClicked={() => setAddTagsOpen(true)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          {document.fileUrl && (
            <Button
              variant="contained"
              startIcon={<OpenInNew />}
              onClick={handleViewDocument}
            >
              Open Document
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <AddTagsDialog
        open={addTagsOpen}
        onClose={() => setAddTagsOpen(false)}
        tags={formattedTags}
        newTags={newTags}
        setNewTags={setNewTags}
        onSave={handleApproveTags}
        saving={savingTags}
        subject={null}
        educationLevel={null}
      />
    </>
  );
};

export default RagDocumentViewer;
